import type {
  DashboardBookingsResponse,
  DashboardEarningsResponse,
  DashboardMetricsResponse,
  GetProfessionalDashboardBookingsParams,
  GetProfessionalDashboardEarningsParams,
  GetProfessionalDashboardMetricsParams
} from '@api/models';

import { ApiClient } from '@website/lib/api';
import type { BookingFilters } from '@website/services/bookings.service';

// Re-export BookingFilters for backwards compatibility with existing dashboard code
export type DashboardFilters = BookingFilters;

// Helper to extract single activityId from string or array (dashboard API only supports single)
const getSingleActivityId = (activityId: string | string[] | undefined): string | undefined => {
  if (!activityId) return undefined;
  return Array.isArray(activityId) ? activityId[0] : activityId;
};

export async function getDashboardMetrics(params: DashboardFilters): Promise<DashboardMetricsResponse> {
  const singleActivityId = getSingleActivityId(params.activityId);
  const apiParams: GetProfessionalDashboardMetricsParams = {
    ...(singleActivityId && { activityId: singleActivityId }),
    ...(params.startDate && { startDate: params.startDate }),
    ...(params.endDate && { endDate: params.endDate })
  };

  return await ApiClient.getProfessionalDashboardMetrics(apiParams);
}

export async function getDashboardBookings(params: DashboardFilters): Promise<DashboardBookingsResponse> {
  const singleActivityId = getSingleActivityId(params.activityId);
  const apiParams: GetProfessionalDashboardBookingsParams = {
    ...(singleActivityId && { activityId: singleActivityId }),
    ...(params.startDate && { startDate: params.startDate }),
    ...(params.endDate && { endDate: params.endDate })
  };

  return await ApiClient.getProfessionalDashboardBookings(apiParams);
}

export async function getDashboardEarnings(params: DashboardFilters): Promise<DashboardEarningsResponse> {
  const singleActivityId = getSingleActivityId(params.activityId);
  const apiParams: GetProfessionalDashboardEarningsParams = {
    ...(singleActivityId && { activityId: singleActivityId }),
    ...(params.startDate && { startDate: params.startDate }),
    ...(params.endDate && { endDate: params.endDate })
  };

  return await ApiClient.getProfessionalDashboardEarnings(apiParams);
}

export function transformDashboardMetrics(response: DashboardMetricsResponse) {
  return {
    totalBookings: {
      count: response.totalBookings || 0,
      percentageChange: response.risePercentages?.totalBookings || 0
    },
    mentees: {
      count: response.totalMentees || 0,
      percentageChange: response.risePercentages?.totalMentees || 0
    },
    completed: {
      count: response.totalCompleted || 0,
      percentageChange: response.risePercentages?.totalCompleted || 0
    },
    cancelled: {
      count: response.totalCancelled || 0,
      percentageChange: response.risePercentages?.totalCancelled || 0
    }
  };
}
